import { useState, useEffect } from "react";
import { FiCheckCircle, FiXCircle, FiAlertCircle as FiAbsent } from "react-icons/fi";
import portalService from "../../../services/portalService";

const extract = (r) => r?.data ?? r;

const GRADE_CONFIG = {
    O: { bg: "#dcfce7", color: "#15803d", label: "Outstanding" },
    "A+": { bg: "#e8f1f8", color: "#002747", label: "Excellent" },
    A: { bg: "#e8f1f8", color: "#003b67", label: "Very Good" },
    "B+": { bg: "#fef9c3", color: "#92400e", label: "Good" },
    B: { bg: "#fef3c7", color: "#d97706", label: "Average" },
    F: { bg: "#fee2e2", color: "#dc2626", label: "Fail" },
    Ab: { bg: "#f3f4f6", color: "#6b7280", label: "Absent" },
    "–": { bg: "#f3f4f6", color: "#6b7280", label: "—" },
};

export default function StudentResults() {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        portalService.getResults()
            .then((r) => {
                const data = extract(r) || [];
                setResults(Array.isArray(data) ? data : []);
                if (data.length > 0) setExpanded({ [data[0]._id]: true });
            })
            .catch((err) => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="loading-center"><div className="spinner" />Loading results…</div>;

    if (error) return (
        <div>
            <div className="page-header"><h1 className="page-title">My Results</h1></div>
            <div style={{ background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "1.25rem", display: "flex", gap: "0.75rem" }}>
                <FiAbsent size={18} color="#dc2626" style={{ flexShrink: 0 }} />
                <div>
                    <div style={{ fontWeight: 700, color: "#dc2626" }}>Could not load results</div>
                    <div style={{ fontSize: 13, fontFamily: "monospace", marginTop: 4 }}>{error}</div>
                </div>
            </div>
        </div>
    );

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">My Results</h1>
                    <p className="page-subtitle">{results.length} exam result{results.length !== 1 ? "s" : ""} recorded</p>
                </div>
            </div>

            {results.length === 0 ? (
                <div className="card"><div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "3rem" }}>No exam results available yet.</div></div>
            ) : (
                <div style={{ display: "grid", gap: "0.85rem" }}>
                    {results.map((result) => {
                        const gc = GRADE_CONFIG[result.grade] || GRADE_CONFIG["–"];
                        const isOpen = expanded[result._id];
                        return (
                            <div key={result._id} className="card" style={{ borderLeft: `4px solid ${gc.color}` }}>
                                {/* Header */}
                                <div style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "1rem 1.25rem", cursor: "pointer" }}
                                    onClick={() => setExpanded((p) => ({ ...p, [result._id]: !p[result._id] }))}>
                                    {/* Grade badge */}
                                    <div style={{ width: 52, height: 52, borderRadius: 12, background: gc.bg, color: gc.color, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                        <div style={{ fontSize: 18, fontWeight: 900, lineHeight: 1 }}>{result.grade}</div>
                                        <div style={{ fontSize: 9, fontWeight: 600, opacity: 0.7 }}>{gc.label}</div>
                                    </div>
                                    {/* Info */}
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 700, fontSize: 14 }}>{result.exam?.title}</div>
                                        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>
                                            {result.exam?.examType}{result.exam?.examDate ? ` · ${result.exam.examDate}` : ""}
                                        </div>
                                    </div>
                                    {/* Score */}
                                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: gc.color }}>
                                            {result.result === "Absent" ? "Absent" : `${result.totalObtained}/${result.totalMaxMarks}`}
                                        </div>
                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{result.percentage}%</div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end", marginTop: 2, fontSize: 12, fontWeight: 600 }}>
                                            {result.result === "Pass" && <><FiCheckCircle size={12} color="#16a34a" /><span style={{ color: "#16a34a" }}>Pass</span></>}
                                            {result.result === "Fail" && <><FiXCircle size={12} color="#dc2626" /><span style={{ color: "#dc2626" }}>Fail</span></>}
                                            {result.result === "Absent" && <><FiAbsent size={12} color="#6b7280" /><span style={{ color: "#6b7280" }}>Absent</span></>}
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 12, color: "var(--text-muted)", flexShrink: 0 }}>{isOpen ? "▲" : "▼"}</div>
                                </div>

                                {/* Subject breakdown */}
                                {isOpen && result.marks?.length > 0 && (
                                    <div style={{ borderTop: "1px solid var(--border)", padding: "0.85rem 1.25rem" }}>
                                        <div style={{ display: "grid", gap: "0.4rem" }}>
                                            {result.marks.map((m) => {
                                                const failed = !m.isAbsent && m.marksObtained < m.passingMarks;
                                                return (
                                                    <div key={m.subjectName} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem 0.75rem", borderRadius: 6, background: failed ? "#fff5f5" : m.isAbsent ? "#f8fafc" : "#f0fdf4" }}>
                                                        <div style={{ fontSize: 13, fontWeight: 500 }}>{m.subjectName}</div>
                                                        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", fontSize: 13 }}>
                                                            <span style={{ color: "var(--text-muted)", fontSize: 11.5 }}>/{m.maxMarks} (pass: {m.passingMarks})</span>
                                                            <span style={{ fontWeight: 700, color: m.isAbsent ? "#6b7280" : failed ? "#dc2626" : "#16a34a", minWidth: 30, textAlign: "right" }}>
                                                                {m.isAbsent ? "AB" : m.marksObtained}
                                                            </span>
                                                            {!m.isAbsent && <span style={{ fontSize: 10, color: failed ? "#dc2626" : "#16a34a", fontWeight: 700 }}>{failed ? "FAIL" : "PASS"}</span>}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {result.remarks && (
                                            <div style={{ marginTop: "0.75rem", fontSize: 12.5, color: "var(--text-muted)", fontStyle: "italic" }}>Remarks: {result.remarks}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}